/**
 * ApiProvider — talks to the real Garson FastAPI backend.
 * Every path comes from endpoints.ts; every error from http.ts.
 * Nothing here invents IDs, tokens or totals: those are server truth.
 */
import type {
  AdminMe,
  AdminContactLeadOut,
  AdminOrderList,
  AIChatInput,
  AIResponse,
  BranchOut,
  ContactLeadCreate,
  ContactLeadOut,
  DashboardSummary,
  MenuItemOut,
  MenuOut,
  RecommendationsOut,
  MockConfirmInput,
  OrderCreate,
  OrderOut,
  OrderPatch,
  PaymentAttemptOut,
  PaymentResult,
  PaymentShareDetailOut,
  RestaurantOut,
  RestaurantTableOut,
  ReviewCreate,
  ReviewOut,
  SpeechTranscriptionOut,
  SessionCreate,
  SplitPaymentCreate,
  SplitPaymentOut,
  TableSessionOut,
} from '@/types/api';
import { endpoints } from '../api/endpoints';
import { ApiError, newIdempotencyKey, request } from '../api/http';
import type { CallOptions, DataProvider } from './provider';

export class ApiProvider implements DataProvider {
  readonly mode = 'api' as const;
  /** API mode serves real restaurant data. */
  readonly isDemo = false;
  /** Payments remain simulated until the backend is wired to a real gateway. */
  readonly paymentIsSimulated = true;

  getRestaurant(restaurantId: string, o?: CallOptions) {
    return request<RestaurantOut>(endpoints.restaurant(restaurantId), { signal: o?.signal });
  }

  getRestaurantMenu(restaurantId: string, o?: CallOptions) {
    return request<MenuOut>(endpoints.restaurantMenu(restaurantId), { signal: o?.signal });
  }

  getRecommendations(
    restaurantId: string,
    params: {
      limit?: number;
      budget_irr?: number;
      party_size?: number;
      dietary?: 'any' | 'vegetarian' | 'vegan' | 'gluten_free';
      spice_max?: number;
      max_prep_minutes?: number;
      cart_item_ids?: string;
    } = {},
    o?: CallOptions,
  ) {
    return request<RecommendationsOut>(endpoints.restaurantRecommendations(restaurantId), {
      query: params,
      signal: o?.signal,
    });
  }

  getBranches(restaurantId: string, o?: CallOptions) {
    return request<BranchOut[]>(endpoints.restaurantBranches(restaurantId), { signal: o?.signal });
  }

  getBranchTables(branchId: string, o?: CallOptions) {
    return request<RestaurantTableOut[]>(endpoints.branchTables(branchId), { signal: o?.signal });
  }

  getMenuItem(itemId: string, o?: CallOptions) {
    return request<MenuItemOut>(endpoints.menuItem(itemId), { signal: o?.signal });
  }

  getReviews(slug: string, limit = 20, o?: CallOptions) {
    return request<ReviewOut[]>(endpoints.restaurantReviews(slug), {
      query: { limit },
      signal: o?.signal,
    });
  }

  /** Canonical session creation: POST /sessions with resolved ids. */
  createSession(input: SessionCreate, o?: CallOptions) {
    return request<TableSessionOut>(endpoints.sessions(), {
      method: 'POST',
      body: input,
      signal: o?.signal,
    });
  }

  getSession(sessionId: string, o?: CallOptions) {
    return request<TableSessionOut>(endpoints.session(sessionId), { signal: o?.signal });
  }

  createOrder(input: OrderCreate, o?: CallOptions) {
    return request<OrderOut>(endpoints.orders(), {
      method: 'POST',
      body: input,
      // Guards against a double tap creating two kitchen tickets.
      idempotencyKey: newIdempotencyKey('order'),
      signal: o?.signal,
    });
  }

  getOrder(orderId: string, o?: CallOptions) {
    return request<OrderOut>(endpoints.order(orderId), { signal: o?.signal });
  }

  /** `expected_revision` is what makes stale-write conflicts a 409 instead of data loss. */
  updateOrder(orderId: string, input: OrderPatch, o?: CallOptions) {
    return request<OrderOut>(endpoints.order(orderId), {
      method: 'PATCH',
      body: input,
      signal: o?.signal,
    });
  }

  confirmOrder(orderId: string, o?: CallOptions) {
    return request<OrderOut>(endpoints.orderConfirm(orderId), {
      method: 'POST',
      signal: o?.signal,
    });
  }

  cancelOrder(orderId: string, o?: CallOptions) {
    return request<OrderOut>(endpoints.orderCancel(orderId), {
      method: 'POST',
      signal: o?.signal,
    });
  }

  createPayment(orderPublicToken: string, o?: CallOptions) {
    return request<PaymentAttemptOut>(endpoints.orderPayments(orderPublicToken), {
      method: 'POST',
      idempotencyKey: newIdempotencyKey('pay'),
      signal: o?.signal,
    });
  }

  confirmMockPayment(paymentToken: string, input: MockConfirmInput, o?: CallOptions) {
    return request<PaymentResult>(endpoints.paymentMockConfirm(paymentToken), {
      method: 'POST',
      body: input,
      idempotencyKey: newIdempotencyKey('payc'),
      signal: o?.signal,
    });
  }

  createSplitPayment(orderPublicToken: string, input: SplitPaymentCreate, o?: CallOptions) {
    return request<SplitPaymentOut>(endpoints.orderSplitPayments(orderPublicToken), {
      method: 'POST',
      body: input,
      signal: o?.signal,
    });
  }

  getSplitPayment(publicToken: string, o?: CallOptions) {
    return request<SplitPaymentOut>(endpoints.splitPayment(publicToken), { signal: o?.signal });
  }

  getPaymentShare(token: string, o?: CallOptions) {
    return request<PaymentShareDetailOut>(endpoints.paymentShare(token), { signal: o?.signal });
  }

  confirmMockShare(shareToken: string, input: MockConfirmInput, o?: CallOptions) {
    return request<PaymentResult>(endpoints.paymentShareMockConfirm(shareToken), {
      method: 'POST',
      body: input,
      idempotencyKey: newIdempotencyKey('share'),
      signal: o?.signal,
    });
  }

  submitReview(input: ReviewCreate, o?: CallOptions) {
    return request<ReviewOut>(endpoints.reviews(), {
      method: 'POST',
      body: input,
      signal: o?.signal,
    });
  }

  /**
   * The only text-assistant call site. If the backend renames or moves this
   * endpoint, this method is the single thing that changes.
   */
  async askTextAssistant(input: AIChatInput, o?: CallOptions) {
    let response: AIResponse;
    try {
      response = await request<AIResponse>(endpoints.aiChat(), {
        method: 'POST',
        body: input,
        // A route recovery can include a bounded 20s proxy rescan before the
        // same Gemini request is retried. Keep the browser alive for that
        // server-side recovery instead of aborting a request that can heal.
        timeoutMs: 60_000,
        signal: o?.signal,
      });
    } catch (error) {
      if (error instanceof ApiError && (error.kind === 'network' || error.kind === 'timeout' || error.kind === 'server')) {
        throw new ApiError(error.kind, error.status, 'اتصال به هوش مصنوعی برقرار نشد. کمی بعد دوباره تلاش کن.', error.details);
      }
      throw error;
    }
    if (response.source !== 'gemini') {
      throw new ApiError('server', 503, 'اتصال به هوش مصنوعی برقرار نشد. کمی بعد دوباره تلاش کن.');
    }
    return response;
  }

  transcribeSpeech(tableSessionToken: string, audio: Blob, durationMs: number, o?: CallOptions) {
    const form = new FormData();
    form.set('table_session_token', tableSessionToken);
    form.set('duration_ms', String(Math.max(1, Math.round(durationMs))));
    form.set('audio', audio, `question.${audio.type.includes('webm') ? 'webm' : 'ogg'}`);
    return request<SpeechTranscriptionOut>(endpoints.aiTranscribe(), {
      method: 'POST',
      body: form,
      timeoutMs: 30_000,
      signal: o?.signal,
    });
  }

  submitContactLead(input: ContactLeadCreate, o?: CallOptions) {
    return request<ContactLeadOut>(endpoints.contactLeads(), {
      method: 'POST',
      body: input,
      signal: o?.signal,
    });
  }

  adminLogin(email: string, password: string, o?: CallOptions) {
    return request<AdminMe>(endpoints.adminLogin(), {
      method: 'POST',
      body: { email, password },
      signal: o?.signal,
    });
  }

  async adminLogout(o?: CallOptions) {
    await request<unknown>(endpoints.adminLogout(), { method: 'POST', signal: o?.signal });
  }

  adminMe(o?: CallOptions) {
    return request<AdminMe>(endpoints.adminMe(), { signal: o?.signal });
  }

  adminOrders(
    params: { status?: string; limit?: number; offset?: number } = {},
    o?: CallOptions,
  ) {
    return request<AdminOrderList>(endpoints.adminOrders(), {
      query: { ...params, restaurant_slug: undefined },
      signal: o?.signal,
    });
  }

  adminSetOrderStatus(publicToken: string, status: string, o?: CallOptions) {
    return request<OrderOut>(endpoints.adminOrderStatus(publicToken), {
      method: 'PATCH',
      body: { status },
      signal: o?.signal,
    });
  }

  adminDashboard(o?: CallOptions) {
    return request<DashboardSummary>(endpoints.adminDashboard(), { signal: o?.signal });
  }

  adminContactLeads(o?: CallOptions) {
    return request<AdminContactLeadOut[]>(endpoints.adminContactLeads(), { signal: o?.signal });
  }

  adminReviews(o?: CallOptions) {
    return request<ReviewOut[]>(endpoints.adminReviews(), { signal: o?.signal });
  }
}
